import React, { useRef, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import styles from './SkeletonLoader.module.css';
import { WithResponsiveProps } from '../../utils/breakpoint/responsiveSSR';
import { responsiveToCssVars } from '../../utils/breakpoint/responsiveToCssVars';

export interface SkeletonLoaderBaseProps {
  /**
   * **[Responsive]** The width of the skeleton
   * Can be a number (pixels) or string (CSS value)
   * @example 200 or "100%" or "50px"
   */
  width?: string | number;

  /**
   * The height of the skeleton
   * Can be a number (pixels) or string (CSS value)
   * @example 20 or "100%" or "2rem"
   */
  height?: string | number;

  /**
   * Number of skeleton lines to display
   * @default 1
   */
  count?: number;

  /**
   * Whether to display the skeleton as a circle
   * When true, height and width should be equal for best results
   * @default false
   */
  circle?: boolean;

  /**
   * Whether to show the loading skeleton
   * When false, renders children or nothing
   * @default true
   */
  isLoading?: boolean;

  /**
   * Children to render when not loading
   */
  children?: React.ReactNode;

  /**
   * Accessible label describing what is loading
   * @example "Loading user profile" or "Loading product details"
   */
  'aria-label'?: string;

  /**
   * Whether the skeleton loader itself should be focusable
   * Set to true when the skeleton represents an interactive element (button, input, link)
   * that was previously focusable in the interface
   * @default false
   */
  elementFocusable?: boolean;

  /**
   * Announces loading state to screen readers after a delay (in ms)
   * @default 300
   */
  announceLoadingDelay?: number;
}

type ResponsiveKeys = 'width';

export type SkeletonLoaderProps = WithResponsiveProps<
  SkeletonLoaderBaseProps,
  ResponsiveKeys
>;

export const SkeletonLoader = ({
  width,
  height,
  count = 1,
  circle = false,
  isLoading = true,
  children,
  'aria-label': ariaLabel,
  elementFocusable = false,
  announceLoadingDelay = 300,
}: SkeletonLoaderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusVisible, setFocusVisible] = useState(false);
  const [announce, setAnnounce] = useState(false);

  const widthVars = responsiveToCssVars(width, {
    varPrefix: '--skeleton-width',
    normalise: (value) => (typeof value === 'number' ? `${value}px` : value),
  });

  // Delay announcement if loading
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setAnnounce(true), announceLoadingDelay);
      return () => clearTimeout(timer);
    }
    setAnnounce(false);
  }, [isLoading, announceLoadingDelay]);

  // Handle focus when replacing focusable elements and actively receives focus
  useEffect(() => {
    if (isLoading && elementFocusable && containerRef.current) {
      // Only focus if no other element currently has focus or if focus was on a related element
      const activeElement = document.activeElement;
      const shouldReceiveFocus =
        !activeElement ||
        activeElement === document.body ||
        activeElement.closest('[data-skeleton-replaced]');

      if (shouldReceiveFocus) {
        containerRef.current.focus();
      }
    }
  }, [isLoading, elementFocusable]);

  // Handle focus-visible state for focusable elements
  useEffect(() => {
    if (!elementFocusable || !containerRef.current) return;

    const element = containerRef.current;

    const handleFocus = () => {
      setFocusVisible(true);
    };

    const handleBlur = () => {
      setFocusVisible(false);
    };

    const handleMouseDown = () => {
      setFocusVisible(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        setFocusVisible(true);
      }
    };

    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [elementFocusable]);

  // Generate appropriate aria-label
  const getAriaLabel = (): string => {
    if (ariaLabel) return ariaLabel;

    if (circle) return 'Loading image';
    if (count > 1) return 'Loading content';
    return 'Loading';
  };

  // When not loading, render children or nothing
  if (!isLoading) {
    return <>{children}</>;
  }

  // Create dynamic gradient using CSS variables
  const highlightGradient = `linear-gradient(90deg, var(--skeleton-highlight-start) 0%, var(--skeleton-highlight-center) 50%, var(--skeleton-highlight-end) 100%)`;

  // Determine wrapper class based on count
  const wrapperClass =
    count > 1
      ? `${styles.skeletonLoader} ${styles.multiLine}`
      : styles.skeletonLoader;

  return (
    <div
      ref={containerRef}
      className={wrapperClass}
      style={{ ...widthVars }}
      tabIndex={elementFocusable ? 0 : -1}
      data-skeleton-loader="true"
      data-focus-visible={elementFocusable && focusVisible ? 'true' : undefined}
    >
      <Skeleton
        height={height}
        count={count}
        circle={circle}
        baseColor="var(--skeleton-base-color)"
        customHighlightBackground={highlightGradient}
        inline={true}
      />
      {/* Dedicated live region for Assistive Technologies */}
      <span
        role="status"
        aria-live="polite"
        className={styles.srOnly}
        data-testid="skeleton-status"
      >
        {announce ? getAriaLabel() : ''}
      </span>
    </div>
  );
};

export default SkeletonLoader;
